import type { Facing } from '@/sprites/katie';
import { TILE_SIZE } from '@/sprites/tiles';

/**
 * A swing of the weapon.
 *
 * Timing lives here rather than in the frame loop so it can be stepped in a
 * test without a renderer: a swing is a duration, a facing, and the arc that
 * was true when it started. Turning mid-swing must not drag the hitbox around
 * with you, or attacking becomes a matter of spinning rather than aiming — so
 * the facing is captured at the start and never re-read.
 */

export interface Aabb {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

const DIRECTIONS: Readonly<Record<Facing, { x: number; y: number }>> = {
  down: { x: 0, y: 1 },
  up: { x: 0, y: -1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

/**
 * The box a swing covers, in world pixels.
 *
 * Centred on the ground ahead and widened across the facing, so a swing
 * catches something standing slightly off the exact line — aiming a 16px box
 * with a thumb is not a skill worth testing.
 */
export function swingArc(
  centreX: number,
  centreY: number,
  facing: Facing,
  reachTiles: number,
): Aabb {
  const direction = DIRECTIONS[facing];
  const reach = reachTiles * TILE_SIZE;
  const across = TILE_SIZE * 1.5;

  const width = direction.x === 0 ? across : reach;
  const height = direction.y === 0 ? across : reach;

  return {
    x: centreX + (direction.x * reach) / 2 - width / 2,
    y: centreY + (direction.y * reach) / 2 - height / 2,
    width,
    height,
  };
}

export function overlaps(a: Aabb, b: Aabb): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

export class Swing {
  /**
   * What this swing has already hit.
   *
   * The arc is tested every simulation step it is live, so that something
   * walking into a swing already in flight has walked into a sword. Without
   * this, standing still inside the arc means being hit on every one of those
   * steps — seventeen times over a third of a second, which killed anything in
   * one press and made the damage number meaningless.
   */
  readonly hits = new Set<number>();

  private elapsed = 0;
  private duration = 0;
  private capturedFacing: Facing = 'down';
  private reach = 1;

  /** Ignored while a swing is already running: no queueing, no cancelling. */
  start(facing: Facing, duration: number, reachTiles: number): boolean {
    if (this.isSwinging) return false;
    this.elapsed = 0;
    this.duration = duration;
    this.hits.clear();
    this.capturedFacing = facing;
    this.reach = reachTiles;
    return true;
  }

  update(dt: number): void {
    if (!this.isSwinging) return;
    this.elapsed = Math.min(this.elapsed + dt, this.duration);
    if (this.elapsed >= this.duration) this.duration = 0;
  }

  get isSwinging(): boolean {
    return this.duration > 0;
  }

  get facing(): Facing {
    return this.capturedFacing;
  }

  /** 0 to 1 across the swing, for picking a frame. */
  get progress(): number {
    return this.duration <= 0 ? 1 : this.elapsed / this.duration;
  }

  /** How far the blade reaches, in tiles. The art scales to it. */
  get reachTiles(): number {
    return this.reach;
  }

  /** Which of `frameCount` frames is showing. */
  frameIndex(frameCount: number): number {
    return Math.min(frameCount - 1, Math.floor(this.progress * frameCount));
  }

  arcAt(centreX: number, centreY: number): Aabb {
    return swingArc(centreX, centreY, this.capturedFacing, this.reach);
  }
}
